import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FaSearch,
  FaChartBar,
  FaChartLine,
  FaChartPie,
  FaChartArea,
  FaTable,
  FaPlus,
  FaArrowLeft,
  FaArrowRight,
  FaEye,
  FaCog,
  FaFilter,
  FaRefresh,
  FaTachometerAlt
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import { ChartRenderer, ReportChart } from '../reports/charts';
import { normalizeReportChartForDisplay } from '../reports/charts/transformWidgetChart';
import {
  getChartTypeDisplayIcon,
  getChartTypeDisplayLabel,
} from '../reports/charts/chartDisplayUtils';
import { splitLoadedReportFields } from '../reports/matrix/matrixReportConfig';
import { useReportCurrency } from '../reports/hooks/useReportCurrency';
import { buildChartDatasetForPreview } from '../../utils/reportChartData';
import {
  getKpiDateFieldCandidates,
  getKpiMetricFieldCandidates,
  normalizeReportFieldForKpi,
  KPI_APPEARANCE_DEFAULTS,
} from '../../utils/kpiWidgetData';
import '../dashboard/css/WidgetBuilder.css';
import './css/HomeWidgetBuilder.css';

const KPI_AGGREGATIONS = ['SUM', 'COUNT', 'AVG', 'MIN', 'MAX'];
const KPI_COMPARISONS = ['LAST_MONTH', 'LAST_WEEK', 'LAST_YEAR', 'NONE'];
const KPI_FORMATS = ['currency', 'number', 'percentage'];

const DEFAULT_KPI_WIDGET_CONFIG = {
  label: '',
  metric_field: '',
  aggregation: 'SUM',
  comparison: 'LAST_MONTH',
  date_field: '',
  format: 'currency',
  show_trend: true,
  decimal_places: 1,
  background_color: KPI_APPEARANCE_DEFAULTS.background_color,
  title_color: KPI_APPEARANCE_DEFAULTS.title_color,
  value_color: KPI_APPEARANCE_DEFAULTS.value_color,
  positive_color: KPI_APPEARANCE_DEFAULTS.positive_color,
  negative_color: KPI_APPEARANCE_DEFAULTS.negative_color,
};

const getConfigStep = (typeValue, includeFullValue) => {
  if (typeValue === 'kpi') {
    return 4;
  }
  if (typeValue === 'dashboard') {
    return 3;
  }

  if (typeValue === 'report') {
    return 4;
  }

  return includeFullValue ? 3 : 4;
};

const dedupeChartsById = (charts = []) => {
  const byId = new Map();
  charts.forEach((chart) => {
    if (!chart?.id) return;
    byId.set(chart.id, { ...(byId.get(chart.id) || {}), ...chart });
  });
  return Array.from(byId.values());
};

const mergeChartIntoList = (charts, chartData, saveMode = 'create') => {
  const list = dedupeChartsById(charts);
  const index = list.findIndex((c) => c.id === chartData.id);
  if (index >= 0) {
    return list.map((c, i) => (i === index ? { ...c, ...chartData } : c));
  }
  if (saveMode === 'update') {
    return list;
  }
  return [...list, chartData];
};

const HomeWidgetBuilder = ({
  isOpen,
  onClose,
  onAddWidget,
  onPreviewWidget,
  editingWidget,
  existingWidgets = []
}) => {
  const rowOptions = Array.from({ length: 25 }, (_, index) => index + 1);
  const getRowOptionLabel = (value) => {
    if (value === 1) return '1 Row';
    if (value === 4) return '4 Rows (Default)';
    return `${value} Rows`;
  };
  const [currentStep, setCurrentStep] = useState(1);
  const [widgetType, setWidgetType] = useState(null); // 'dashboard', 'report', or 'kpi'
  const [dashboards, setDashboards] = useState([]);
  const [filteredDashboards, setFilteredDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportCharts, setReportCharts] = useState([]);
  const [selectedChart, setSelectedChart] = useState(null);
  const [includeFullReport, setIncludeFullReport] = useState(false);
  // Report data processing states (following ReportDataViewer pattern)
  const [reportInfo, setReportInfo] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedColumnGroups, setSelectedColumnGroups] = useState([]);
  const [summarizedFields, setSummarizedFields] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [groupingInfo, setGroupingInfo] = useState(null);
  const [reportConfig, setReportConfig] = useState(null);
  const [loadedReportFields, setLoadedReportFields] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const {
    reportConversionBaseCode,
    reportConversionLookup,
    multiCurrencyEnabled,
  } = useReportCurrency();
  const [widgetConfig, setWidgetConfig] = useState({
    name: '',
    widget_type: '', // 'dashboard' or 'report' / 'chart'
    chart_type: 'bar',
    column_span: 6, // Default to half of 12-column grid
    row_span: 4, // Default height
    refresh_rate: 300, // 5 minutes
    show_filters: true,
    show_export: true,
    show_header: true, // Control whether to show dashboard/component header
    sort_order: '', // Sort order number
    chart_config: {},
    widget_config: { ...DEFAULT_KPI_WIDGET_CONFIG },
    dashboard_id: null // For dashboard widgets
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showChartBuilder, setShowChartBuilder] = useState(false);
  const [editingChart, setEditingChart] = useState(null);
  const [savingChart, setSavingChart] = useState(false);
  const chartSaveInProgressRef = useRef(false);
  const previewSubmitInProgressRef = useRef(false);

  useEffect(() => {
    if (isOpen) {

      if (editingWidget) {
        console.log('HomeWidgetBuilder - Editing widget:', editingWidget);
        const rawWidgetType = editingWidget.widget_type || editingWidget.type || 'dashboard';
        const editingNorm = String(rawWidgetType).toLowerCase();
        const resolvedType =
          editingNorm === 'dashboard'
            ? 'dashboard'
            : editingNorm === 'kpi'
              ? 'kpi'
              : 'report';
        const isChartWidget = rawWidgetType === 'chart';
        setWidgetType(resolvedType);

        const isFullReportWidget =
          resolvedType === 'report' && !isChartWidget && editingNorm !== 'kpi';
        setIncludeFullReport(isFullReportWidget);

        const editingKpiConfig = {
          ...DEFAULT_KPI_WIDGET_CONFIG,
          ...(editingWidget.widget_config || {}),
        };
        if (editingNorm === 'kpi' && !editingKpiConfig.label) {
          editingKpiConfig.label = editingWidget.name || '';
        }

        setWidgetConfig({
          name:
            editingNorm === 'kpi'
              ? editingKpiConfig.label || editingWidget.name || ''
              : editingWidget.name || '',
          widget_type:
            editingNorm === 'kpi'
              ? 'kpi'
              : isFullReportWidget
                ? 'report'
                : (editingWidget.widget_type || editingWidget.type || 'dashboard'),
          chart_type:
            editingNorm === 'kpi'
              ? 'kpi'
              : editingWidget.chart_type || (isFullReportWidget ? 'report' : 'bar'),
          column_span: editingWidget.column_span || 6,
          row_span: editingWidget.row_span || 4,
          refresh_rate: editingWidget.refresh_rate || 300,
          show_filters: editingWidget.show_filters !== undefined ? editingWidget.show_filters : true,
          show_export: editingWidget.show_export !== undefined ? editingWidget.show_export : true,
          show_header: editingWidget.show_header !== undefined ? editingWidget.show_header : true,
          sort_order: editingWidget.sort_order || '',
          chart_config: isFullReportWidget || editingNorm === 'kpi' ? {} : (editingWidget.chart_config || {}),
          widget_config: editingKpiConfig,
          dashboard_id: editingWidget.dashboard_id || editingWidget.parent_id || null
        });

        if (isFullReportWidget || editingNorm === 'kpi') {
          setSelectedChart(null);
        } else {
          const chartData = editingWidget.chart_config || editingWidget.selectedChart || {};
          setSelectedChart(chartData && chartData.id ? chartData : null);
        }

        if (resolvedType === 'dashboard') {
          setCurrentStep(3);
          setSelectedDashboard({
            id: editingWidget.dashboard_id || editingWidget.parent_id,
            name: editingWidget.name?.split(' - ')[0] || 'Unknown Dashboard'
          });
        } else {
          const reportIdForWidget =
            editingWidget.parent_id || editingWidget.report_id;
          setSelectedReport({
            id: reportIdForWidget,
            report_label:
              editingWidget.report_label ||
              editingWidget.name?.split(' - ')[0] ||
              'Unknown Report',
            name:
              editingWidget.report_label ||
              editingWidget.name?.split(' - ')[0] ||
              'Unknown Report'
          });
          loadReportData(reportIdForWidget);

          if (editingNorm === 'kpi') {
            setCurrentStep(3);
          } else if (rawWidgetType === 'report') {
            setCurrentStep(getConfigStep('report', true));
          } else {
            const chartData = editingWidget.chart_config || editingWidget.selectedChart || {};
            setSelectedChart(chartData);
            setCurrentStep(getConfigStep('report', false));
          }
        }
      } else {
        resetBuilder();
      }
    }
  }, [isOpen, editingWidget]);

  useEffect(() => {
    filterReports();
  }, [reports, searchTerm]);

  useEffect(() => {
    filterDashboards();
  }, [dashboards, searchTerm]);

  useEffect(() => {
    const maxStep = getConfigStep(widgetType, includeFullReport);
    if (widgetType && currentStep > maxStep) {
      setCurrentStep(maxStep);
    }
  }, [widgetType, includeFullReport]);

  const resetBuilder = () => {
    setCurrentStep(1);
    setWidgetType(null);
    setSelectedDashboard(null);
    setSelectedReport(null);
    setSelectedChart(null);
    setShowChartBuilder(false);
    setEditingChart(null);
    setIncludeFullReport(false);
    setSearchTerm('');
    chartSaveInProgressRef.current = false;
    previewSubmitInProgressRef.current = false;
    setSelectedFields([]);
    setSummarizedFields({});
    setSelectedGroups([]);
    setSelectedColumnGroups([]);
    setPreviewData([]);
    setGroupingInfo(null);
    setReportConfig(null);
    setLoadedReportFields([]);
    setWidgetConfig({
      name: '',
      widget_type: '',
      chart_type: 'bar',
      column_span: 6, // Default to half of 12-column grid
      row_span: 4, // Default height
      refresh_rate: 300,
      show_filters: true,
      show_export: true,
      show_header: true,
      chart_config: {},
      widget_config: { ...DEFAULT_KPI_WIDGET_CONFIG },
      dashboard_id: null
    });
  };

  const loadDashboards = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading dashboards from API...');
      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.fetchAll();

      console.log('Dashboards API response:', response);

      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setDashboards(response.data.data);
        console.log('Dashboards loaded successfully:', response.data.data.length, 'dashboards');
      } else {
        setError('Failed to load dashboards');
        console.error('API returned invalid data:', response);
      }
    } catch (err) {
      console.error('Error loading dashboards:', err);
      setError('Error loading dashboards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const reportApi = new DataApi('reports'); // Fixed: use 'reports' (plural) to match backend route
      console.log('Loading reports from API...');
      const response = await reportApi.fetchAll();

      console.log('Reports API response:', response.data);

      if (response.data.success) {
        setReports(response.data.data);
        console.log('Reports loaded successfully:', response.data.data.length, 'reports');
      } else {
        setError('Failed to load reports');
        console.error('API returned success: false', response.data);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Error loading reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterDashboards = () => {
    if (!searchTerm.trim()) {
      setFilteredDashboards(dashboards);
    } else {
      const filtered = dashboards.filter(dashboard =>
        dashboard.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (dashboard.description && dashboard.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredDashboards(filtered);
    }
  };

  const filterReports = () => {
    if (!searchTerm.trim()) {
      setFilteredReports(reports);
    } else {
      const filtered = reports.filter(report =>
        report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (report.description && report.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredReports(filtered);
    }
  };


  // Function to get the next available sort order
  const getNextSortOrder = () => {
    if (!existingWidgets || existingWidgets.length === 0) {
      return 0; // First widget starts at 0
    }

    // Find the maximum sort order from existing widgets
    const maxSortOrder = Math.max(...existingWidgets.map(widget => widget.sort_order || 0));

    // Return the next available sort order
    return maxSortOrder + 1;
  };

  const handleWidgetTypeSelect = (type) => {
    console.log('Widget type selected:', type);
    const nextSortOrder = getNextSortOrder();
    setWidgetType(type);
    setSelectedDashboard(null);
    setSelectedReport(null);
    setSelectedChart(null);
    setError(null);
    setSearchTerm('');
    setWidgetConfig(prev => ({
      ...prev,
      name: '',
      widget_type:
        type === 'report' ? 'chart' : type === 'kpi' ? 'kpi' : type,
      sort_order: nextSortOrder,
      chart_type: type === 'dashboard' ? 'dashboard' : type === 'kpi' ? 'kpi' : 'bar',
      chart_config: type === 'dashboard' || type === 'kpi' ? {} : prev.chart_config,
      widget_config: type === 'kpi' ? { ...DEFAULT_KPI_WIDGET_CONFIG } : prev.widget_config,
    }));

    if (type === 'dashboard') {
      loadDashboards();
      setIncludeFullReport(false);
    } else {
      loadReports();
      setIncludeFullReport(false);
    }
    setCurrentStep(2);
  };

  const handleDashboardSelect = (dashboard) => {
    console.log('Dashboard selected:', dashboard);
    setSelectedDashboard(dashboard);
    setWidgetConfig(prev => ({
      ...prev,
      name: `${dashboard.name} Widget`,
      dashboard_id: dashboard.id,
      sort_order: prev.sort_order // Preserve the sort order set in handleTypeSelect
    }));

    setCurrentStep(3); // Go to widget configuration
  };

  const handleReportSelect = async (report) => {
    console.log('Report selected:', report);
    const reportDetails = {
      id: report.id,
      report_label: report.report_label || report.name,
      name: report.report_label || report.name,
      description: report.description
    };
    setSelectedReport(reportDetails);
    setError(null);
    setIncludeFullReport(false);
    setSelectedChart(null);

    if (widgetType === 'kpi') {
      const defaultLabel = `${reportDetails.report_label} KPI`;
      setWidgetConfig(prev => ({
        ...prev,
        name: prev.name || prev.widget_config?.label || defaultLabel,
        widget_type: 'kpi',
        chart_type: 'kpi',
        report_label: reportDetails.report_label,
        widget_config: {
          ...DEFAULT_KPI_WIDGET_CONFIG,
          ...(prev.widget_config || {}),
          label: prev.widget_config?.label || prev.name || defaultLabel,
        },
        sort_order: prev.sort_order
      }));
      try {
        await loadReportData(report.id);
      } catch (err) {
        console.error('Error loading report data:', err);
        setError('Error loading report data. Please try again.');
      }
      setCurrentStep(3);
      return;
    }

    setWidgetConfig(prev => ({
      ...prev,
      name: `${reportDetails.report_label} Widget`,
      report_label: reportDetails.report_label,
      sort_order: prev.sort_order
    }));

    try {
      await loadReportData(report.id);
    } catch (err) {
      console.error('Error loading report data:', err);
      setError('Error loading report data. Please try again.');
    }

    setCurrentStep(3);
  };

  const handleKpiConfigChange = (field, value) => {
    setWidgetConfig((prev) => {
      const nextConfig = {
        ...(prev.widget_config || DEFAULT_KPI_WIDGET_CONFIG),
        [field]: value,
      };
      if (field === 'aggregation') {
        const candidates = getKpiMetricFieldCandidates(loadedReportFields, value);
        const currentMetric = nextConfig.metric_field;
        if (
          currentMetric &&
          !candidates.some((f) => f.field_name === currentMetric)
        ) {
          nextConfig.metric_field = '';
        }
      }
      const next = { ...prev, widget_config: nextConfig };
      if (field === 'label') {
        next.name = typeof value === 'string' ? value : '';
      }
      return next;
    });
  };

  const kpiAggregation =
    (widgetConfig.widget_config || {}).aggregation || 'SUM';

  const kpiNumericFields = useMemo(
    () => getKpiMetricFieldCandidates(loadedReportFields, kpiAggregation),
    [loadedReportFields, kpiAggregation]
  );

  const kpiDateFields = useMemo(
    () => getKpiDateFieldCandidates(loadedReportFields),
    [loadedReportFields]
  );

  const handleIncludeFullReportToggle = (checked) => {
    setIncludeFullReport(checked);
    setSelectedChart(null);
    setWidgetConfig(prev => ({
      ...prev,
      widget_type: checked ? 'report' : 'chart',
      chart_type: checked ? 'report' : prev.chart_type,
      chart_config: checked ? {} : prev.chart_config
    }));
  };

  // Load report data following ReportDataViewer pattern
  const loadReportData = async (reportId) => {
    setPreviewLoading(true);
    setError(null);

    try {
      // First, get the report configuration (same as ReportDataViewer)
      const reportBuilderApi = new DataApi('reportbuilder');
      const reportResponse = await reportBuilderApi.get(`/${reportId}`);

      if (!reportResponse?.data?.success) {
        throw new Error('Failed to load report configuration');
      }

      const report = reportResponse.data.data;
      console.log('WidgetBuilder - Loaded report for viewer:', report);

      setReportConfig(report.config || null);
      const normalizedReportFields = (report.fields || [])
        .map(normalizeReportFieldForKpi)
        .filter(Boolean);
      setLoadedReportFields(normalizedReportFields);

      // Set report information (same as ReportDataViewer)
      setReportInfo({
        id: report.id,
        name: report.name,
        report_label: report.report_label,
        description: report.description,
        object_name: report.object_name,
        object_label: report.object_label,
        report_type: report.report_type,
        is_public: report.is_public,
        filter_logic: report.filter_logic,
        filter_custom_logic: report.filter_custom_logic,
        report_objects: report.report_objects || []
      });

      // Process report fields and groups (same as ReportDataViewer)
      if (report.fields && report.fields.length > 0) {
        const fields = report.fields.map(field => ({
          id: field.field_config_id,
          field_name: field.field_name,
          field_label: field.field_label,
          data_type: field.data_type,
          object_name: field.object_name,
          is_aggregated: field.is_aggregated,
          aggregate_function: field.aggregate_function,
          is_grouped: field.is_grouped,
          sort_order: field.sort_order
        }));

        // Helper function to convert database aggregation values to dropdown values
        const convertAggregationValue = (dbValue) => {
          const mapping = {
            'average': 'avg',
            'sum': 'sum',
            'count': 'count',
            'min': 'min',
            'max': 'max'
          };
          return mapping[dbValue] || dbValue;
        };

        const { rowGroups, columnGroups, selectedFieldsList, summarized } =
          splitLoadedReportFields(
            report.fields.map((field) => ({
              ...field,
              field_config_id: field.field_config_id,
              group_axis: field.group_axis,
            })),
            convertAggregationValue
          );

        setSelectedFields(selectedFieldsList);
        setSelectedGroups(rowGroups);
        setSelectedColumnGroups(columnGroups);
        setSummarizedFields(summarized);

        // Load charts from the report data (same as ReportDataViewer)
        console.log('WidgetBuilder - Report charts data:', report.charts);
        console.log('WidgetBuilder - Is charts array?', Array.isArray(report.charts));
        console.log('WidgetBuilder - Charts length:', report.charts ? report.charts.length : 'undefined');

        if (report.charts && Array.isArray(report.charts) && report.charts.length > 0) {
          console.log('WidgetBuilder - Setting charts from report data:', report.charts);
          setReportCharts(dedupeChartsById(report.charts));
        } else {
          console.log('WidgetBuilder - No charts found in report data');
          setReportCharts([]);
        }
      }

      // Fetch actual report data for charts (same as ReportDataViewer)
      await fetchReportData(report);

    } catch (error) {
      console.error('Error loading report data:', error);
      setError('Error loading report data. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Fetch actual report data (same as ReportDataViewer)
  const fetchReportData = async (report, filters = {}) => {
    setPreviewLoading(true);
    console.log('WidgetBuilder - fetchReportData:', {
      reportId: report.id,
      limit: 10, // Limit to 10 records for widget preview
      filters: filters
    });

    try {
      // Use the same route as ReportDataViewer to fetch actual report data
      const dataApi = new DataApi('object');
      const response = await dataApi.post('/view/report', {
        reportId: report.id,
        limit: 10000, // Limit to 10 records for widget preview
        filters: filters
      });

      if (response?.data?.success) {
        const rawData = response.data.data;
        const grouping = response.data.grouping;

        console.log('WidgetBuilder - Report data loaded:', rawData);
        console.log('WidgetBuilder - Grouping info:', grouping);

        setPreviewData(rawData);
        setGroupingInfo(grouping);

      } else {
        throw new Error(response?.data?.message || 'Failed to fetch report data');
      }

    } catch (err) {
      console.error('WidgetBuilder - Error fetching report data:', err);
      setError('Error fetching report data. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };


  const chartPreviewBundle = useMemo(
    () =>
      buildChartDatasetForPreview({
        rawData: previewData,
        groupingInfo,
        selectedFields,
        selectedGroups,
        selectedColumnGroups,
        summarizedFields,
        reportFields: loadedReportFields,
        reportConfig,
        reportCharts,
        chartConfig: selectedChart,
        baseCurrencyCode: reportConversionBaseCode,
        exchangeRateLookup: reportConversionLookup,
        multiCurrencyEnabled,
      }),
    [
      previewData,
      groupingInfo,
      selectedFields,
      selectedGroups,
      selectedColumnGroups,
      summarizedFields,
      loadedReportFields,
      reportConfig,
      reportCharts,
      selectedChart,
      reportConversionBaseCode,
      reportConversionLookup,
      multiCurrencyEnabled,
    ]
  );

  const summarizedData = chartPreviewBundle.dataset || {};
  const chartPreviewData =
    chartPreviewBundle.previewData?.length > 0
      ? chartPreviewBundle.previewData
      : previewData;

  const transformChartForDisplay = (chart, summarizedDataForChart = null) =>
    normalizeReportChartForDisplay(chart, summarizedDataForChart);

  const handleChartSelect = async (chart) => {
    console.log('WidgetBuilder - Chart selected:', chart);
    console.log('WidgetBuilder - Current selectedFields:', selectedFields);
    console.log('WidgetBuilder - Current summarizedFields:', summarizedFields);
    console.log('WidgetBuilder - Current selectedGroups:', selectedGroups);

    const chartConfig = normalizeReportChartForDisplay(
      { ...chart, title: chart.title },
      summarizedData
    );
    setSelectedChart(chartConfig);

    setWidgetConfig(prev => ({
      ...prev,
      chart_type: chartConfig.chartType || chartConfig.chart_type,
      chart_config: chartConfig
    }));

    // Only auto-advance to step 4 if we're not in step 3 for report widgets
    if (!(currentStep === 3 && widgetType === 'report')) {
      setCurrentStep(4);
    }
  };

  const handleConfigChange = (field, value) => {
    setWidgetConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateNewChart = () => {
    setEditingChart(null);
    setShowChartBuilder(true);
  };

  const handleCloseChartBuilder = () => {
    setShowChartBuilder(false);
    setEditingChart(null);
    chartSaveInProgressRef.current = false;
  };

  const handleSaveChart = async (chartData, saveMode = 'create') => {
    if (chartSaveInProgressRef.current || !chartData?.id) {
      return;
    }
    chartSaveInProgressRef.current = true;
    setSavingChart(true);
    setError(null);

    try {
      const nextCharts = mergeChartIntoList(reportCharts, chartData, saveMode);
      setReportCharts(nextCharts);

      if (selectedReport?.id && reportInfo) {
        const reportBuilderApi = new DataApi('reportbuilder');
        const response = await reportBuilderApi.put(`/reports/${selectedReport.id}`, {
          name: reportInfo.name,
          report_label: reportInfo.report_label,
          description: reportInfo.description,
          object_name: reportInfo.object_name,
          report_type: reportInfo.report_type,
          is_public: reportInfo.is_public,
          charts: nextCharts,
        });
        if (!response?.data?.success && !response?.data?.data) {
          throw new Error(response?.data?.message || 'Failed to save chart to report');
        }
      }

      const normalized = normalizeReportChartForDisplay(chartData, summarizedData);
      setSelectedChart(normalized);
      setWidgetConfig((prev) => ({
        ...prev,
        widget_type: 'chart',
        chart_type: normalized.chartType || normalized.chart_type,
        chart_config: normalized,
      }));
      setShowChartBuilder(false);
      setEditingChart(null);
    } catch (err) {
      console.error('HomeWidgetBuilder - Error saving chart:', err);
      setError('Failed to save chart. Please try again.');
      throw err;
    } finally {
      setSavingChart(false);
      chartSaveInProgressRef.current = false;
    }
  };

  const handlePreview = async () => {
    if (previewSubmitInProgressRef.current || loading) {
      return;
    }
    previewSubmitInProgressRef.current = true;

    try {
      setLoading(true);

      const isDashboardWidgetType = widgetType === 'dashboard';
      const isKpiSelection = widgetType === 'kpi';
      const isFullReportWidget = widgetType === 'report' && includeFullReport;
      const isReportChartWidget = widgetType === 'report' && !includeFullReport;

      if (isKpiSelection) {
        const kpiCfg = widgetConfig.widget_config || DEFAULT_KPI_WIDGET_CONFIG;
        if (!kpiCfg.metric_field) {
          setError('Please select a metric field for the KPI.');
          setLoading(false);
          previewSubmitInProgressRef.current = false;
          return;
        }
        if (
          kpiCfg.comparison &&
          kpiCfg.comparison !== 'NONE' &&
          !kpiCfg.date_field
        ) {
          setError('Please select a date field for period comparison.');
          setLoading(false);
          previewSubmitInProgressRef.current = false;
          return;
        }
      }

      const parentId = isDashboardWidgetType
        ? selectedDashboard?.id
        : selectedReport?.id;
      const reportLabel =
        selectedReport?.report_label || selectedReport?.name || 'Report';

      let defaultName;
      if (isDashboardWidgetType) {
        defaultName = selectedDashboard?.name || 'Dashboard Widget';
      } else if (isKpiSelection) {
        defaultName = `${reportLabel} - KPI`;
      } else if (isFullReportWidget) {
        defaultName = `${reportLabel} - Report Viewer`;
      } else {
        defaultName =
          selectedChart?.title ||
          `${reportLabel} - ${getChartTypeLabel(selectedChart?.chart_type || widgetConfig.chart_type)}`;
      }

      const kpiLabel = isKpiSelection
        ? (
            (widgetConfig.widget_config || {}).label ||
            widgetConfig.name ||
            ''
          ).trim()
        : '';
      const widgetName = isKpiSelection
        ? kpiLabel || defaultName
        : widgetConfig.name || defaultName;

      console.log('## Selected Chart:', selectedChart);
      console.log('## Selected Dashboard:', selectedDashboard);

      const widgetPreviewData = {
        id: editingWidget ? editingWidget.id : `preview-${Date.now()}`,
        name: widgetName,
        widget_type: isKpiSelection
          ? 'kpi'
          : isFullReportWidget
            ? 'report'
            : (widgetConfig.widget_type || (widgetType === 'report' ? 'chart' : widgetType)),
        type: isDashboardWidgetType
          ? 'dashboard'
          : isKpiSelection
            ? 'KPI'
            : isFullReportWidget
              ? 'report'
              : 'chart',
        chart_type: isDashboardWidgetType
          ? 'dashboard'
          : isKpiSelection
            ? 'kpi'
            : isFullReportWidget
              ? 'report'
              : (selectedChart?.chartType || selectedChart?.chart_type || widgetConfig.chart_type),
        widget_config: isKpiSelection
          ? {
              ...(widgetConfig.widget_config || DEFAULT_KPI_WIDGET_CONFIG),
              label: widgetName,
            }
          : null,
        column_span: widgetConfig.column_span || 6,
        row_span: widgetConfig.row_span || 4,
        refresh_rate: widgetConfig.refresh_rate,
        show_filters: widgetConfig.show_filters,
        show_export: widgetConfig.show_export,
        show_header: isDashboardWidgetType ? widgetConfig.show_header : true,
        sort_order: widgetConfig.sort_order,
        chart_config: isReportChartWidget
          ? (widgetConfig.chart_config || selectedChart || {})
          : isKpiSelection || isFullReportWidget
            ? null
            : widgetConfig.chart_config || selectedChart || {},
        dashboard_id: widgetConfig.dashboard_id,
        report_id: selectedReport?.id,
        parent_id: parentId,
        report_label: widgetConfig.report_label || reportLabel,
        chart_id: isReportChartWidget ? selectedChart?.id : null,
        isPreview: !editingWidget,
        x: editingWidget ? editingWidget.x : 0,
        y: editingWidget ? editingWidget.y : 0,
        previewData: isReportChartWidget
          ? (editingWidget ? (editingWidget.previewData || chartPreviewData) : chartPreviewData)
          : [],
        summarizedData: isReportChartWidget
          ? (editingWidget ? (editingWidget.summarizedData || editingWidget.dataset || summarizedData) : summarizedData)
          : null,
        groupingInfo: isReportChartWidget
          ? (editingWidget ? (editingWidget.groupingInfo || groupingInfo) : groupingInfo)
          : null,
        selectedFields: isReportChartWidget
          ? (editingWidget ? (editingWidget.selectedFields || selectedFields) : selectedFields)
          : [],
        summarizedFields: isReportChartWidget
          ? (editingWidget ? (editingWidget.summarizedFields || summarizedFields) : summarizedFields)
          : {},
        selectedGroups: isReportChartWidget
          ? (editingWidget ? (editingWidget.selectedGroups || selectedGroups) : selectedGroups)
          : [],
        selectedColumnGroups: isReportChartWidget
          ? (editingWidget ? (editingWidget.selectedColumnGroups || selectedColumnGroups) : selectedColumnGroups)
          : [],
        selectedChart: isReportChartWidget ? selectedChart : null,
        selectedDashboard: selectedDashboard,
        selectedReport: selectedReport,
        dataset: isReportChartWidget && editingWidget ? editingWidget.dataset : undefined
      };

      if (isFullReportWidget) {
        widgetPreviewData.chart_config = null;
        widgetPreviewData.chart_id = null;
      }

      console.log('WidgetBuilder - Creating/updating widget:', widgetPreviewData);
      console.log('WidgetBuilder - Chart configuration being passed:', {
        selectedFields: selectedFields,
        summarizedFields: summarizedFields,
        selectedGroups: selectedGroups,
        previewData: previewData,
        summarizedData: summarizedData,
        selectedChart: selectedChart,
        widgetConfigChartConfig: widgetConfig.chart_config,
        isEditing: !!editingWidget
      });

      // Call the preview function
      if (onPreviewWidget) {
        await onPreviewWidget(widgetPreviewData);
      }

      // Close the builder
      onClose();

    } catch (error) {
      console.error('WidgetBuilder - Error creating/updating widget:', error);
      setError('Failed to create/update widget');
    } finally {
      setLoading(false);
      previewSubmitInProgressRef.current = false;
    }
  };


  const getChartIcon = (chartType) => getChartTypeDisplayIcon(chartType);

  const getChartTypeLabel = (chartType) => getChartTypeDisplayLabel(chartType);

  if (!isOpen) return null;

  const handleFooterBack = () => {
    if (editingWidget || currentStep <= 1) return;

    if (currentStep === 2) {
      setCurrentStep(1);
      setWidgetType(null);
      setSelectedDashboard(null);
      setSelectedReport(null);
      setSearchTerm('');
      return;
    }

    const configStep = getConfigStep(widgetType, includeFullReport);

    if (currentStep === configStep) {
      if (widgetType === 'dashboard') {
        setSelectedDashboard(null);
        setCurrentStep(2);
      } else if (widgetType === 'kpi') {
        setCurrentStep(3);
      } else if (widgetType === 'report') {
        setCurrentStep(3);
      }
      return;
    }

    if (currentStep === 3) {
      if (widgetType === 'dashboard') {
        setSelectedDashboard(null);
        setCurrentStep(2);
      } else {
        setSelectedReport(null);
        setSelectedChart(null);
        setIncludeFullReport(false);
        setCurrentStep(2);
      }
    }
  };

  const showFooterBack = !editingWidget && currentStep > 1;

  const configStepIndex = getConfigStep(widgetType, includeFullReport);
  const shouldShowNextButton =
    (currentStep === 3 && widgetType === 'report') ||
    (currentStep === 3 && widgetType === 'kpi');
  const isNextDisabled =
    (shouldShowNextButton &&
      widgetType === 'report' &&
      !includeFullReport &&
      !selectedChart) ||
    (shouldShowNextButton &&
      widgetType === 'kpi' &&
      !(widgetConfig.widget_config || {}).metric_field);

  const steps =
    widgetType === 'report'
      ? ['Type', 'Select', 'Chart', 'Config']
      : widgetType === 'kpi'
        ? ['Type', 'Select', 'KPI', 'Config']
        : widgetType === 'dashboard'
          ? ['Type', 'Select', 'Config']
          : ['Type', 'Select', 'Config'];

  const renderReportOptions = () => {
    const showFullReportToggle = widgetType === 'report';
    const isShowingCharts = !showFullReportToggle || !includeFullReport;

    return (
      <div className="home-widget-report-options">
        {showFullReportToggle && (
          <div className="home-widget-report-mode-toggle">
            <button
              type="button"
              className={`home-widget-report-mode-btn ${includeFullReport ? 'active' : ''}`}
              onClick={() => handleIncludeFullReportToggle(true)}
            >
              <span className="home-widget-report-mode-title">Full Report</span>
              <span className="home-widget-report-mode-subtitle">Embed complete report viewer</span>
            </button>
            <button
              type="button"
              className={`home-widget-report-mode-btn ${!includeFullReport ? 'active' : ''}`}
              onClick={() => handleIncludeFullReportToggle(false)}
            >
              <span className="home-widget-report-mode-title">Specific Chart</span>
              <span className="home-widget-report-mode-subtitle">Pick from report charts</span>
            </button>
          </div>
        )}

        {showFullReportToggle && includeFullReport && (
          <p className="home-widget-form-help-text">
            The widget will render the entire report using the standard report viewer. Sort order and sizing can be adjusted in the next step.
          </p>
        )}

        {isShowingCharts && (
          <div className="home-widget-chart-options">
            <div className="existing-charts">
              <h4>Existing Charts</h4>
              {reportCharts && reportCharts.length > 0 ? (
                <div className="charts-list">
                  {reportCharts.map((chart) => {
                    const chartId = chart.id || chart.chart_id || chart.tempId;
                    const isSelected = selectedChart?.id === chartId || selectedChart?.id === chart.id;
                    const chartTypeValue = chart.chart_type || chart.chartType || 'chart';

                    return (
                      <div
                        key={chartId || `${chart.title}-${chartTypeValue}`}
                        className={`chart-preview-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleChartSelect(chart)}
                      >
                        <div className="chart-preview-header">
                          <div className="chart-icon">
                            {getChartIcon(chartTypeValue)}
                          </div>
                          <div className="chart-info">
                            <h5>{chart.title || 'Untitled Chart'}</h5>
                            <p>{getChartTypeLabel(chartTypeValue)}</p>
                          </div>
                          <div className="chart-select-btn">
                            <FaArrowRight />
                          </div>
                        </div>
                        <div className="chart-preview-content">
                          {previewLoading ? (
                            <div className="chart-preview-placeholder">
                              <div className="placeholder-icon">
                                {getChartIcon(chartTypeValue)}
                              </div>
                              <p>Loading chart preview...</p>
                              <small>{chart.title || 'Untitled Chart'}</small>
                            </div>
                          ) : previewData.length > 0 && summarizedData && Object.keys(summarizedData).length > 0 ? (
                            <div className="chart-preview">
                              <ChartRenderer
                                chart={transformChartForDisplay(chart, summarizedData)}
                                selectedFields={selectedFields}
                                summarizedFields={summarizedFields}
                                selectedGroups={selectedGroups}
                                selectedColumnGroups={selectedColumnGroups}
                                summarizedData={summarizedData}
                                previewData={chartPreviewData}
                                baseCurrencyCode={reportConversionBaseCode}
                                exchangeRateLookup={reportConversionLookup}
                                multiCurrencyEnabled={multiCurrencyEnabled}
                                isPreview={true}
                                displayAuditInfo={false}
                              />
                            </div>
                          ) : (
                            <div className="chart-preview-placeholder">
                              <div className="placeholder-icon">
                                {getChartIcon(chartTypeValue)}
                              </div>
                              <p>Chart Preview</p>
                              <small>{chart.title || 'Untitled Chart'}</small>
                              <div className="chart-config-info">
                                <div className="config-item">
                                  <strong>Type:</strong> {getChartTypeLabel(chartTypeValue)}
                                </div>
                                {(chart.x_axis_field || chart.xAxisField) && (
                                  <div className="config-item">
                                    <strong>X-Axis:</strong> {chart.x_axis_field || chart.xAxisField}
                                  </div>
                                )}
                                {(chart.y_axis_field || chart.yAxisField) && (
                                  <div className="config-item">
                                    <strong>Y-Axis:</strong> {chart.y_axis_field || chart.yAxisField}
                                  </div>
                                )}
                                {(chart.group_field || chart.groupField) && (
                                  <div className="config-item">
                                    <strong>Group By:</strong> {chart.group_field || chart.groupField}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-charts">
                  <p>No existing charts found for this report.</p>
                </div>
              )}
            </div>

            <div className="create-new-chart">
              <button
                type="button"
                className="create-chart-btn"
                onClick={handleCreateNewChart}
                disabled={previewLoading || savingChart}
              >
                <FaPlus />
                Create New Chart
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="widget-builder-overlay">
      <div className="widget-builder">
        {/* Header */}
        <div className="builder-header">
          <div className="builder-title">
            <h2>{editingWidget ? 'Edit Widget' : 'Add Widget'}</h2>
            <button className="builder-close-btn" onClick={onClose}>×</button>
          </div>
          <div className="builder-steps">
            {steps.map((label, index) => (
              <div key={label} className={`step ${currentStep >= index + 1 ? 'active' : ''}`}>
                <span className="step-number">{index + 1}</span>
                <span className="step-label">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="builder-content">
          {error && (
            <div className="builder-error">
              <p>{error}</p>
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {/* Step 1: Choose Widget Type */}
          {currentStep === 1 && (
            <div className="builder-step">
              <div className="step-header">
                <h3>Choose Widget Type</h3>
                <p>Select the type of widget you want to add</p>
              </div>

              <div className="widget-type-options">
                <button
                  type="button"
                  className={`widget-type-option ${widgetType === 'report' ? 'active' : ''}`}
                  onClick={() => handleWidgetTypeSelect('report')}
                >
                  <div className="widget-type-icon report">
                    <FaChartBar />
                  </div>
                  <div className="widget-type-content">
                    <h4>Report Widget</h4>
                    <p>Add a report or chart as a widget to your home screen</p>
                  </div>
                </button>
                <button
                  type="button"
                  className={`widget-type-option ${widgetType === 'kpi' ? 'active' : ''}`}
                  onClick={() => handleWidgetTypeSelect('kpi')}
                >
                  <div className="widget-type-icon kpi">
                    <FaTachometerAlt />
                  </div>
                  <div className="widget-type-content">
                    <h4>KPI Widget</h4>
                    <p>Single metric from a report with optional period comparison</p>
                  </div>
                </button>
                <button
                  type="button"
                  className={`widget-type-option ${widgetType === 'dashboard' ? 'active' : ''}`}
                  onClick={() => handleWidgetTypeSelect('dashboard')}
                >
                  <div className="widget-type-icon dashboard">
                    <FaChartPie />
                  </div>
                  <div className="widget-type-content">
                    <h4>Dashboard Widget</h4>
                    <p>Add a dashboard as a widget to your home screen</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Choose Report (report or KPI) */}
          {currentStep === 2 && (widgetType === 'report' || widgetType === 'kpi') && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={handleFooterBack}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>Choose a Report</h3>
                <p>
                  {widgetType === 'kpi'
                    ? 'Select the report that defines filters, joins, and security for this KPI'
                    : 'Select the report that will be displayed as a widget'}
                </p>
              </div>

              <div className="search-section">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search reports..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="reports-list">
                {loading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading reports...</p>
                  </div>
                ) : filteredReports.length === 0 ? (
                  <div className="empty-state">
                    <p>No reports found</p>
                  </div>
                ) : (
                  filteredReports.map((report) => (
                    <div
                      key={report.id}
                      className="report-item"
                      onClick={() => handleReportSelect(report)}
                    >
                      <div className="report-icon">
                        <FaChartBar />
                      </div>
                      <div className="report-info">
                        <h4 className="report-name">{report.report_label}</h4>
                        {report.description && (
                          <p className="report-description">{report.description}</p>
                        )}
                        <div className="report-meta">
                          <span className="report-type">Report</span>
                        </div>
                      </div>
                      <div className="report-arrow">
                        <FaArrowRight />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 2: Choose Dashboard or Task */}
          {currentStep === 2 && widgetType === 'dashboard' && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={handleFooterBack}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>Choose a Dashboard</h3>
                <p>Select the dashboard that will be displayed as a widget</p>
              </div>

              <div className="search-section">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder={`Search ${widgetType === 'dashboard' ? 'dashboards' : 'reports'}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {widgetType === 'dashboard' ? (
                <div className="dashboards-list">
                  {loading ? (
                    <div className="loading-state">
                      <div className="loading-spinner"></div>
                      <p>Loading dashboards...</p>
                    </div>
                  ) : filteredDashboards.length === 0 ? (
                    <div className="empty-state">
                      <p>No dashboards found</p>
                    </div>
                  ) : (
                    filteredDashboards.map((dashboard) => (
                      <div
                        key={dashboard.id}
                        className="dashboard-item"
                        onClick={() => handleDashboardSelect(dashboard)}
                      >
                        <div className="dashboard-icon">
                          <FaTachometerAlt />
                        </div>
                        <div className="dashboard-info">
                          <h4 className="dashboard-name">{dashboard.name}</h4>
                          {dashboard.description && (
                            <p className="dashboard-description">{dashboard.description}</p>
                          )}
                          <div className="dashboard-meta">
                            <span className="dashboard-type">Dashboard</span>
                            <span className="dashboard-date">
                              Created: {new Date(dashboard.createddate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="dashboard-arrow">
                          <FaArrowRight />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="reports-list">
                  {loading ? (
                    <div className="loading-state">
                      <div className="loading-spinner"></div>
                      <p>Loading reports...</p>
                    </div>
                  ) : filteredReports.length === 0 ? (
                    <div className="empty-state">
                      <p>No reports found</p>
                    </div>
                  ) : (
                    filteredReports.map((report) => (
                      <div
                        key={report.id}
                        className="report-item"
                        onClick={() => handleReportSelect(report)}
                      >
                        <div className="report-icon">
                          <FaChartBar />
                        </div>
                        <div className="report-info">
                          <h4 className="report-name">{report.report_label}</h4>
                          {report.description && (
                            <p className="report-description">{report.description}</p>
                          )}
                          <div className="report-meta">
                            <span className="report-type">Report</span>
                            <span className="report-date">
                              Created: {new Date(report.createddate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="report-arrow">
                          <FaArrowRight />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Choose Chart (for reports) */}
          {currentStep === 3 && widgetType === 'report' && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={handleFooterBack}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>Choose Chart Type</h3>
                <p>Select an existing chart to display in your widget</p>
              </div>

              {selectedReport && (
                <div className="selected-report">
                  <div className="report-summary">
                    <FaChartBar className="report-icon" />
                    <div>
                      <h4>{selectedReport.report_label}</h4>
                      <p>Selected Report</p>
                    </div>
                  </div>
                </div>
              )}

              {renderReportOptions()}

            </div>
          )}

          {/* Step 3: KPI configuration */}
          {currentStep === 3 && widgetType === 'kpi' && (
            <div className="builder-step">
              <div className="step-header">
                <button className="back-btn" onClick={() => setCurrentStep(2)}>
                  <FaArrowLeft />
                  Back
                </button>
                <h3>KPI Configuration</h3>
                <p>Define the metric, aggregation, and comparison settings</p>
              </div>

              {selectedReport && (
                <div className="selected-report">
                  <p>
                    <strong>Report:</strong> {selectedReport.report_label || selectedReport.name}
                  </p>
                </div>
              )}

              <div className="widget-config-form kpi-config-form">
                <div className="config-group">
                  <label htmlFor="kpi-widget-label">Label</label>
                  <input
                    id="kpi-widget-label"
                    type="text"
                    value={(widgetConfig.widget_config || {}).label ?? ''}
                    onChange={(e) => handleKpiConfigChange('label', e.target.value)}
                    placeholder="e.g. Total Revenue"
                  />
                  <p className="config-hint">Shown as the widget title on the home screen.</p>
                </div>
                <div className="config-group">
                  <label>Metric field</label>
                  <select
                    value={(widgetConfig.widget_config || {}).metric_field || ''}
                    onChange={(e) => handleKpiConfigChange('metric_field', e.target.value)}
                    disabled={previewLoading}
                  >
                    <option value="">
                      {previewLoading
                        ? 'Loading report fields...'
                        : kpiNumericFields.length === 0
                          ? 'No fields available on this report'
                          : 'Select field...'}
                    </option>
                    {kpiNumericFields.map((f) => (
                      <option key={`${f.object_name}_${f.field_name}`} value={f.field_name}>
                        {f.field_label || f.field_name}
                        {f.object_name ? ` (${f.object_name})` : ''}
                      </option>
                    ))}
                  </select>
                  {!previewLoading && kpiNumericFields.length === 0 && selectedReport && (
                    <p className="config-hint">
                      Add a number, currency, or summarized amount field to the report first.
                    </p>
                  )}
                </div>
                <div className="config-group">
                  <label>Aggregation</label>
                  <select
                    value={(widgetConfig.widget_config || {}).aggregation || 'SUM'}
                    onChange={(e) => handleKpiConfigChange('aggregation', e.target.value)}
                  >
                    {KPI_AGGREGATIONS.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="config-group">
                  <label>Comparison</label>
                  <select
                    value={(widgetConfig.widget_config || {}).comparison || 'NONE'}
                    onChange={(e) => handleKpiConfigChange('comparison', e.target.value)}
                  >
                    {KPI_COMPARISONS.map((c) => (
                      <option key={c} value={c}>
                        {c.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="config-group">
                  <label>Date field (for comparison)</label>
                  <select
                    value={(widgetConfig.widget_config || {}).date_field || ''}
                    onChange={(e) => handleKpiConfigChange('date_field', e.target.value)}
                  >
                    <option value="">None</option>
                    {kpiDateFields.map((f) => (
                      <option key={`${f.object_name}_${f.field_name}`} value={f.field_name}>
                        {f.field_label || f.field_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="config-group">
                  <label>Format</label>
                  <select
                    value={(widgetConfig.widget_config || {}).format || 'number'}
                    onChange={(e) => handleKpiConfigChange('format', e.target.value)}
                  >
                    {KPI_FORMATS.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="config-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={(widgetConfig.widget_config || {}).show_trend !== false}
                      onChange={(e) => handleKpiConfigChange('show_trend', e.target.checked)}
                    />
                    Show trend %
                  </label>
                </div>

                <div className="config-section kpi-appearance-section">
                  <h4>Appearance</h4>
                  <div className="config-group kpi-color-row">
                    <label htmlFor="kpi-bg-color">Background color</label>
                    <input
                      id="kpi-bg-color"
                      type="color"
                      value={
                        (widgetConfig.widget_config || {}).background_color ||
                        '#ffffff'
                      }
                      onChange={(e) =>
                        handleKpiConfigChange('background_color', e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="kpi-color-reset-btn"
                      onClick={() =>
                        handleKpiConfigChange(
                          'background_color',
                          KPI_APPEARANCE_DEFAULTS.background_color
                        )
                      }
                    >
                      Default (transparent)
                    </button>
                  </div>
                  <div className="config-group kpi-color-row">
                    <label htmlFor="kpi-title-color">Title color</label>
                    <input
                      id="kpi-title-color"
                      type="color"
                      value={
                        (widgetConfig.widget_config || {}).title_color ||
                        KPI_APPEARANCE_DEFAULTS.title_color
                      }
                      onChange={(e) =>
                        handleKpiConfigChange('title_color', e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="kpi-color-reset-btn"
                      onClick={() =>
                        handleKpiConfigChange(
                          'title_color',
                          KPI_APPEARANCE_DEFAULTS.title_color
                        )
                      }
                    >
                      Default
                    </button>
                  </div>
                  <div className="config-group kpi-color-row">
                    <label htmlFor="kpi-value-color">Main value color ($ amount)</label>
                    <input
                      id="kpi-value-color"
                      type="color"
                      value={
                        (widgetConfig.widget_config || {}).value_color ||
                        KPI_APPEARANCE_DEFAULTS.value_color
                      }
                      onChange={(e) =>
                        handleKpiConfigChange('value_color', e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="kpi-color-reset-btn"
                      onClick={() =>
                        handleKpiConfigChange(
                          'value_color',
                          KPI_APPEARANCE_DEFAULTS.value_color
                        )
                      }
                    >
                      Default
                    </button>
                  </div>
                  <div className="config-group kpi-color-row">
                    <label htmlFor="kpi-positive-color">Positive trend color (up arrow only)</label>
                    <input
                      id="kpi-positive-color"
                      type="color"
                      value={
                        (widgetConfig.widget_config || {}).positive_color ||
                        KPI_APPEARANCE_DEFAULTS.positive_color
                      }
                      onChange={(e) =>
                        handleKpiConfigChange('positive_color', e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="kpi-color-reset-btn"
                      onClick={() =>
                        handleKpiConfigChange(
                          'positive_color',
                          KPI_APPEARANCE_DEFAULTS.positive_color
                        )
                      }
                    >
                      Default
                    </button>
                  </div>
                  <div className="config-group kpi-color-row">
                    <label htmlFor="kpi-negative-color">Negative trend color (down arrow only)</label>
                    <input
                      id="kpi-negative-color"
                      type="color"
                      value={
                        (widgetConfig.widget_config || {}).negative_color ||
                        KPI_APPEARANCE_DEFAULTS.negative_color
                      }
                      onChange={(e) =>
                        handleKpiConfigChange('negative_color', e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="kpi-color-reset-btn"
                      onClick={() =>
                        handleKpiConfigChange(
                          'negative_color',
                          KPI_APPEARANCE_DEFAULTS.negative_color
                        )
                      }
                    >
                      Default
                    </button>
                  </div>
                  <p className="config-hint">
                    Main value color controls $4.8K-style numbers. Positive/negative colors
                    apply only when the trend arrow points up or down. Clear background for
                    a transparent tile.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Widget Configuration (for reports) */}
          {currentStep === 4 && widgetType === 'report' && (
            <div className="builder-step">
              <div className="step-header">
                {!editingWidget && (
                  <button
                    className="back-btn"
                    onClick={handleFooterBack}
                  >
                    <FaArrowLeft />
                    Back
                  </button>
                )}
                <h3>Configure Report Widget</h3>
                <p>Set up your report widget display and behavior</p>
              </div>

              <div className="config-form">
                <div className="form-group">
                  <label htmlFor="widget-name">Widget Name</label>
                  <input
                    type="text"
                    id="widget-name"
                    value={widgetConfig.name}
                    onChange={(e) => handleConfigChange('name', e.target.value)}
                    placeholder="Enter widget name"
                    className="form-input"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="column-span">Width</label>
                    <select
                      id="column-span"
                      value={widgetConfig.column_span}
                      onChange={(e) => handleConfigChange('column_span', parseInt(e.target.value))}
                    >
                      <option value={3}>3 Columns (25%)</option>
                      <option value={4}>4 Columns (33%)</option>
                      <option value={6}>6 Columns (50%)</option>
                      <option value={8}>8 Columns (67%)</option>
                      <option value={9}>9 Columns (75%)</option>
                      <option value={12}>12 Columns (Full Width)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="row-span">Rows (Height)</label>
                    <select
                      id="row-span"
                      value={widgetConfig.row_span}
                      onChange={(e) => handleConfigChange('row_span', parseInt(e.target.value))}
                    >
                      {rowOptions.map((value) => (
                        <option key={value} value={value}>
                          {getRowOptionLabel(value)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="sort-order">Sort Order</label>
                  <input
                    type="number"
                    id="sort-order"
                    value={widgetConfig.sort_order}
                    onChange={(e) => handleConfigChange('sort_order', parseInt(e.target.value) || 0)}
                    placeholder="Enter sort order"
                    className="form-input"
                  />
                </div>

                <div className="config-actions">
                  <button
                    className="add-widget-btn"
                    onClick={handlePreview}
                    disabled={loading}
                  >
                    <FaPlus />
                    {editingWidget ? 'Update Widget' : 'Add Widget'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Widget Configuration (for KPI) */}
          {currentStep === 4 && widgetType === 'kpi' && (
            <div className="builder-step">
              <div className="step-header">
                {!editingWidget && (
                  <button
                    className="back-btn"
                    onClick={handleFooterBack}
                  >
                    <FaArrowLeft />
                    Back
                  </button>
                )}
                <h3>Configure KPI Widget</h3>
                <p>Set up your KPI widget display and behavior</p>
              </div>

              {selectedReport && (
                <div className="selected-report">
                  <div className="report-summary">
                    <FaTachometerAlt className="report-icon" />
                    <div>
                      <h4>{selectedReport.report_label || selectedReport.name}</h4>
                      <p>
                        Metric: {(widgetConfig.widget_config || {}).metric_field || '—'} (
                        {(widgetConfig.widget_config || {}).aggregation || 'SUM'})
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="config-form">
                <div className="form-group">
                  <label htmlFor="kpi-widget-label-step4">Label</label>
                  <input
                    type="text"
                    id="kpi-widget-label-step4"
                    value={(widgetConfig.widget_config || {}).label ?? ''}
                    onChange={(e) => handleKpiConfigChange('label', e.target.value)}
                    placeholder="e.g. Total Revenue"
                    className="form-input"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="kpi-column-span">Width</label>
                    <select
                      id="kpi-column-span"
                      value={widgetConfig.column_span}
                      onChange={(e) => handleConfigChange('column_span', parseInt(e.target.value))}
                    >
                      <option value={3}>3 Columns (25%)</option>
                      <option value={4}>4 Columns (33%)</option>
                      <option value={6}>6 Columns (50%)</option>
                      <option value={8}>8 Columns (67%)</option>
                      <option value={9}>9 Columns (75%)</option>
                      <option value={12}>12 Columns (Full Width)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="kpi-row-span">Rows (Height)</label>
                    <select
                      id="kpi-row-span"
                      value={widgetConfig.row_span}
                      onChange={(e) => handleConfigChange('row_span', parseInt(e.target.value))}
                    >
                      {rowOptions.map((value) => (
                        <option key={value} value={value}>
                          {getRowOptionLabel(value)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="kpi-sort-order">Sort Order</label>
                  <input
                    type="number"
                    id="kpi-sort-order"
                    value={widgetConfig.sort_order}
                    onChange={(e) => handleConfigChange('sort_order', parseInt(e.target.value) || 0)}
                    placeholder="Enter sort order"
                    className="form-input"
                  />
                </div>

                <div className="config-actions">
                  <button
                    className="add-widget-btn"
                    onClick={handlePreview}
                    disabled={loading}
                  >
                    <FaPlus />
                    {editingWidget ? 'Update Widget' : 'Add Widget'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Widget Configuration (for dashboards) */}
          {currentStep === 3 && widgetType === 'dashboard' && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={handleFooterBack}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>Configure Dashboard Widget</h3>
                <p>Set the widget size and display options</p>
              </div>

              {selectedDashboard && (
                <div className="selected-dashboard">
                  <div className="dashboard-summary">
                    <FaTachometerAlt className="dashboard-icon" />
                    <div>
                      <h4>{selectedDashboard.name}</h4>
                      <p>Selected Dashboard</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="widget-config-form">
                <div className="config-section">
                  <h4>Widget Settings</h4>

                  <div className="form-group">
                    <label htmlFor="widget-name">Widget Name</label>
                    <input
                      id="widget-name"
                      type="text"
                      value={widgetConfig.name}
                      onChange={(e) => handleConfigChange('name', e.target.value)}
                      placeholder="Enter widget name..."
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="column-span">Columns (Width)</label>
                    <select
                      id="column-span"
                      value={widgetConfig.column_span}
                      onChange={(e) => handleConfigChange('column_span', parseInt(e.target.value))}
                    >
                      <option value={1}>1 Column</option>
                      <option value={2}>2 Columns</option>
                      <option value={3}>3 Columns</option>
                      <option value={4}>4 Columns</option>
                      <option value={5}>5 Columns</option>
                      <option value={6}>6 Columns (Default)</option>
                      <option value={7}>7 Columns</option>
                      <option value={8}>8 Columns</option>
                      <option value={9}>9 Columns</option>
                      <option value={10}>10 Columns</option>
                      <option value={11}>11 Columns</option>
                      <option value={12}>12 Columns (Full Width)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="row-span">Rows (Height)</label>
                    <select
                      id="row-span"
                      value={widgetConfig.row_span}
                      onChange={(e) => handleConfigChange('row_span', parseInt(e.target.value))}
                    >
                      {rowOptions.map((value) => (
                        <option key={value} value={value}>
                          {getRowOptionLabel(value)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="sort-order">Sort Order</label>
                    <input
                      id="sort-order"
                      type="number"
                      min="1"
                      value={widgetConfig.sort_order || ''}
                      onChange={(e) => handleConfigChange('sort_order', parseInt(e.target.value) || '')}
                      placeholder="Enter sort order number..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={widgetConfig.show_header}
                        onChange={(e) => handleConfigChange('show_header', e.target.checked)}
                      />
                      <span className="checkbox-text">Show Header</span>
                    </label>
                    <p className="form-help-text">
                      Display the dashboard header with title and controls
                    </p>
                  </div>
                </div>

                <div className="config-actions">
                  <button
                    className="add-widget-btn"
                    onClick={handlePreview}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        {editingWidget ? 'Updating Widget...' : 'Adding Widget...'}
                      </>
                    ) : (
                      <>
                        <FaPlus />
                        {editingWidget ? 'Update Widget' : 'Add Widget'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>

        <ReportChart
          isOpen={showChartBuilder}
          onClose={handleCloseChartBuilder}
          reportData={
            selectedReport
              ? {
                  id: selectedReport.id,
                  name: reportInfo?.name || selectedReport.name,
                  report_label: reportInfo?.report_label || selectedReport.report_label,
                }
              : null
          }
          onSaveChart={handleSaveChart}
          summarizedData={summarizedData}
          summarizedFields={summarizedFields}
          previewData={previewData}
          selectedGroups={selectedGroups}
          selectedColumnGroups={selectedColumnGroups}
          selectedFields={selectedFields}
          baseCurrencyCode={reportConversionBaseCode}
          exchangeRateLookup={reportConversionLookup}
          multiCurrencyEnabled={multiCurrencyEnabled}
          editingChart={editingChart}
        />

        {/* Footer */}
        <div className="builder-footer">
          <div className="footer-actions">
            <div className="footer-buttons">
              <button
                className="cancel-btn"
                onClick={onClose}
              >
                Cancel
              </button>
              {showFooterBack && (
                <button
                  type="button"
                  className="footer-back-btn"
                  onClick={handleFooterBack}
                >
                  <FaArrowLeft />
                  Back
                </button>
              )}
              {shouldShowNextButton && (
                <button
                  className="next-btn"
                  onClick={() => setCurrentStep(configStepIndex)}
                  disabled={isNextDisabled}
                >
                  Next
                  <FaArrowRight />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeWidgetBuilder;
